import React, { useMemo, useState } from "react";
import styles from "./documentVariations.module.css";
import useTrackerVariations from "../../../api/useTrackerVariationsApi";
import InlineSpinner from "../../Common/InlineSpinner/InlineSpinner";
import {
  TAB_DEROGATION,
  TAB_NATIONAL,
  TAB_TEMP_VAR,
  TAB_REGIONAL,
  getVariationTabs,
} from "./documentVariations.utils";

const EMPTY_ARRAY = [];

const variationReadMoreText = [
  "A standards Variation is an application for permission to depart or use an alternative approach from the originally specified amber requirement in a Network Rail Standard. You cannot apply for a variation against a red requirement as they are mandated with no ability to vary. You do not need to apply for a variation against a green requirement as they are to be used unless alternative solutions are followed (these should be documented). Standard Variation Applications are an official form of control to record approved non-compliances. A Temporary Variation is an application for permission to depart or use an alternative approach from the originally specified requirement in a Network Rail or Railway Group Standard for a specified period. A Derogation is an application for permission to permanently depart or use an alternative approach from the originally specified requirement in a Network Rail or Railway Group Standard. These are usually covering very specific areas therefore we have only included the number of these variations, the detail of these can be seen on Tracker.",
  "Variations pending standard change are applications for permission to depart or use an alternative approach from the originally specified requirement in a Network Rail Standard until that standard is changed and therefore are usually of more interest to a wider audience. We have provided a high-level summary of approved applications against this standard in the below tables, these are split between those approved for use nationally and those approved for use in specific regions only. If you are interested in using a current variation, please review the details carefully in Tracker to make sure that you can comply with the requirements and that your area/project is in scope of the approval. If you would like to raise a new variation, please do so by visiting Tracker, more information on this can be found under our FAQ page. Please note that only those internal to Network Rail can access Tracker, if you are not internal to Network Rail and you'd like more information on a variation or would like to submit a variation please contact your Network Rail representative.",
];

const TRACKER_ACCESS_PORTAL_URL = "https://networkrail-accessportal.oncreate.app/i/portal/login";

const isTrackerGuidanceTab = (tabKey) =>
  tabKey === TAB_DEROGATION ||
  tabKey === TAB_TEMP_VAR;

const isPendingChangeTab = (tabKey) =>
  tabKey === TAB_REGIONAL ||
  tabKey === TAB_NATIONAL;

const StatCard = ({ imageSrc, label, count, isActive, onClick }) => (
  <div
    className={`${styles.statCard} ${isActive ? styles.statCardActive : ""}`}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    <div className={styles.statIcon}>
      <img src={imageSrc} alt={label} />
    </div>
    <div className={styles.statBody}>
      <p className={styles.statLabel}>{label}</p>
      <p className={styles.statCount}>{count ?? ""}</p>
    </div>
  </div>
);

const VariationTable = ({ rows, title }) => (
  <div className={styles.tableSection}>
    <h4 className={styles.tableTitle}>{title}</h4>
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colVariationNo} />
        <col className={styles.colArea} />
        <col className={styles.colVariationTitle} />
        <col className={styles.colActivity} />
        <col className={styles.colModule} />
        <col className={styles.colClause} />
        <col className={styles.colProposal} />
      </colgroup>
      <thead>
        <tr>
          <th>
            <span className={styles.compactHeader}>
              Variation
              <br />
              No.
            </span>
          </th>
          <th>Area</th>
          <th>Variation Title</th>
          <th>Activity</th>
          <th>Module</th>
          <th>Clause</th>
          <th>Proposal</th>
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? (
          rows.map((row, i) => (
            <tr key={row.trackerReferenceNumber ?? i}>
              <td>{row.trackerReferenceNumber ?? "-"}</td>
              <td>{row.areaOfVariation ?? "-"}</td>
              <td>{row.title ?? row.titleOfCertificate ?? "-"}</td>
              <td>{row.activity ?? "-"}</td>
              <td>{row.module ?? "-"}</td>
              <td>{row.clauseNumber ?? "-"}</td>
              <td>{row.proposedAlternative ?? "-"}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={7} className={styles.emptyRow}>
              No data available.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const TrackerGuidance = () => (
  <div className={styles.guidanceSection}>
    <p>
      <strong>For Network Rail users</strong> - The Standards Variation Tracker
      System is accessed via the Digital Factory Single Sign on Access Portal,
      please{" "}
      <a
        href={TRACKER_ACCESS_PORTAL_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        click here
      </a>
      . For further information please visit the{" "}
      <a href="/faq">FAQ page</a>.
    </p>
    <p>
      <strong>For external users</strong> - Please contact your Network Rail
      representative for more information.
    </p>
  </div>
);

const DocumentVariations = ({ standardId, standardNumber }) => {
  const [activeTab, setActiveTab] = useState(TAB_REGIONAL);
  const [isReadMoreExpanded, setIsReadMoreExpanded] = useState(false);

  const {
    variationPendingChange,
    derogation,
    temporaryVariation,
    isLoading,
    isFetched,
    error,
  } = useTrackerVariations({
    standardId,
    standardNumber,
  });

  const tabs = useMemo(
    () =>
      getVariationTabs({
        variationPendingChange: variationPendingChange ?? EMPTY_ARRAY,
        derogation: derogation ?? EMPTY_ARRAY,
        temporaryVariation: temporaryVariation ?? EMPTY_ARRAY,
      }),
    [variationPendingChange, derogation, temporaryVariation],
  );

  const displayTabs = useMemo(
    () =>
      isFetched
        ? tabs
        : tabs.map((tab) => ({
            ...tab,
            count: null,
          })),
    [isFetched, tabs],
  );

  const activeTabData = tabs.find((t) => t.key === activeTab) ?? tabs[0];
  const activeRows = activeTabData?.rows ?? [];

  const handleTabClick = (tab) => {
    setActiveTab(tab.key);
  };

  return (
    <div className={styles.page}>
      <div className={styles.readMoreSection}>
        <h5 className={styles.readMoreTitle}>
          Information about variations
        </h5>
        <p className={styles.readMoreSubtitle} mt={2}>
          Please note - these figures and details may not include all variations approved for modules. 
          For complete details, please refer to the Standards Variation Tracker System (Tracker).
        </p>
        {isReadMoreExpanded ? (
          <>
            <div className={styles.readMoreContent}>
              {variationReadMoreText.map((paragraph, index) => {
                const faqPhrase = "FAQ page";
                if (paragraph.includes(faqPhrase)) {
                  const [before, after] = paragraph.split(faqPhrase);
                  return (
                    <p key={index}>
                      {before}
                      <a href="/faq" style={{ color: "#0072ce", cursor: "pointer" }} onMouseEnter={(e) => e.target.style.color = "#004d71"} onMouseLeave={(e) => e.target.style.color = "#0072ce"}>
                        FAQ page
                      </a>
                      {after}
                    </p>
                  );
                }

                return <p key={index}>{paragraph}</p>;
              })}
            </div>
            <button
              type="button"
              className={styles.readMoreButton}
              onClick={() => setIsReadMoreExpanded(false)}
            >
              Read less
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.readMoreButton}
            onClick={() => setIsReadMoreExpanded(true)}
          >
            Read more
          </button>
        )}
      </div>

      <div
        className={`${styles.statsGrid} ${isReadMoreExpanded ? styles.statsGridExpanded : ""}`}
      >
        {displayTabs.map((tab) => (
          <StatCard
            key={tab.key}
            imageSrc={tab.icon}
            label={tab.label}
            count={tab.count}
            isActive={activeTab === tab.key}
            onClick={() => handleTabClick(tab)}
          />
        ))}
      </div>

      {isLoading && (
        <div className={styles.emptyRow} style={{ textAlign: "center", padding: "24px" }}>
          <InlineSpinner />
        </div>
      )}

      {!isLoading && error && (
        <p className={styles.emptyRow} style={{ textAlign: "center", padding: "24px", color: "red" }}>
          Failed to load variations.
        </p>
      )}

      {!isLoading && !error && (
        isTrackerGuidanceTab(activeTab) ? (
          <TrackerGuidance />
        ) : isPendingChangeTab(activeTab) ? (
          <>
            <VariationTable
              rows={activeRows}
              title={activeTabData?.tableTitle ?? "Variations"}
            />
            <TrackerGuidance />
          </>
        ) : (
          <VariationTable
            rows={activeRows}
            title={activeTabData?.tableTitle ?? "Variations"}
          />
        )
      )}
    </div>
  );
};

export default DocumentVariations;

